import styled from "styled-components";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Link } from "react-router-dom";

export const OuterContainer = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  gap: ${({ theme }) => theme.spacing.regular};
  width: min(100%, 790px);
  align-items: center;

  @media (max-width: 520px) {
    gap: ${({ theme }) => theme.spacing.medium};
  }

  @media (min-width: 2500px) {
    width: min(100%, 1080px);
    gap: ${({ theme }) => theme.spacing.medium};
  }
  @media (min-width: 3000px) {
    width: min(100%, 44dvw);
    gap: ${({ theme }) => theme.spacing.large};
  }
  @media (min-width: 4500px) {
    width: min(100%, 48dvw);
  }
`;

export const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
  color: ${({ theme }) => theme.colors.offWhite};
  text-align: center;

  h2 {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.h1.fontSize};
    font-weight: ${({ theme }) => theme.typography.h1.fontWeight};
    text-transform: uppercase;

    @media (max-width: 520px) {
      font-size: 2.25rem;
    }

    @media (min-width: 2500px) {
      font-size: 4rem;
    }
    @media (min-width: 3400px) {
      font-size: 5.25rem;
      word-spacing: ${({ theme }) => theme.spacing.small};
    }
    @media (min-width: 3800px) {
      font-size: 5.75rem;
    }
    @media (min-width: 4500px) {
      font-size: 6.25rem;
    }
    @media (min-width: 5000px) {
      font-size: 6.75rem;
    }
  }

  p {
    margin: 0;
    font-weight: ${({ theme }) => theme.fontWeight.semibold};

    @media (max-width: 520px) {
      font-size: ${({ theme }) => theme.typography.regular.fontSize};
    }

    @media (min-width: 2500px) {
      font-size: 1.75rem;
    }
    @media (min-width: 3000px) {
      font-size: 2rem;
      font-weight: ${({ theme }) => theme.fontWeight.medium};
    }
    @media (min-width: 3400px) {
      font-size: 2.5rem;
    }
    @media (min-width: 3800px) {
      font-size: 3rem;
    }
    @media (min-width: 4500px) {
      font-size: 3.25rem;
    }
    @media (min-width: 5000px) {
      font-size: 3.75rem;
    }
  }

  @media (min-width: 3000px) {
    gap: ${({ theme }) => theme.spacing.regular};
  }
`;

export const Application = styled.form`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  gap: ${({ theme }) => theme.spacing.regular};
  padding: ${({ theme }) => theme.padding.large};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background-color: ${({ theme }) => theme.colors.offWhite};
  color: ${({ theme }) => theme.colors.darkBlue};

  @media (max-width: 520px) {
    padding: 0;
    background-color: transparent;
    color: ${({ theme }) => theme.colors.offWhite};
  }

  @media (min-width: 2500px) {
    border-radius: ${({ theme }) => theme.borderRadius.large};
  }
  @media (min-width: 3000px) {
    gap: 24px;
    padding: 52px;
  }
  @media (min-width: 3800px) {
    gap: ${({ theme }) => theme.spacing.medium};
    border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  }
  @media (min-width: 4500px) {
    gap: 52px;
    padding: 82px;
  }
`;

export const InputColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xsmall};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};

  label {
    font-size: ${({ theme }) => theme.typography.body.fontSize};

    @media (max-width: 520px) {
      font-size: ${({ theme }) => theme.typography.medium.fontSize};
    }

    @media (min-width: 2500px) {
      font-size: 1.75rem;
    }
    @media (min-width: 3000px) {
      font-size: 2rem;
    }
    @media (min-width: 3400px) {
      font-size: 2.5rem;
    }
    @media (min-width: 3800px) {
      font-size: 3rem;
    }
    @media (min-width: 4500px) {
      font-size: 3.25rem;
    }
    @media (min-width: 5000px) {
      font-size: 3.75rem;
    }
  }
`;

const controlStyles = `
  width: 100%;
  box-sizing: border-box;
  border: 1px solid transparent;
  outline: none;
  font: inherit;
`;

export const FieldControl = styled(InputText)`
  ${controlStyles}
  min-height: 42px;
  padding: ${({ theme }) => theme.padding.xsmall}
    ${({ theme }) => theme.padding.small};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.darkBlue};
  box-shadow: ${({ theme }) => theme.shadow.xsmall};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.accent};
    box-shadow: 0 0 0 3px rgba(18, 109, 200, 0.2);
  }

  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }

  @media (min-width: 2500px) {
    min-height: 62px;
    font-size: 1.75rem;
    border-radius: ${({ theme }) => theme.borderRadius.regular};
    padding: ${({ theme }) => theme.padding.small};
  }
  @media (min-width: 3000px) {
    min-height: 74px;
    font-size: 2rem;
    border-radius: ${({ theme }) => theme.borderRadius.medium};
    padding: ${({ theme }) => theme.padding.small}
      ${({ theme }) => theme.padding.regular};
  }
  @media (min-width: 3400px) {
    min-height: 92px;
    font-size: 2.5rem;
    border-radius: ${({ theme }) => theme.borderRadius.large};
    padding: ${({ theme }) => theme.padding.regular}
      ${({ theme }) => theme.padding.medium};
  }
  @media (min-width: 3800px) {
    min-height: 112px;
    font-size: 3rem;
  }
  @media (min-width: 4500px) {
    font-size: 3.25rem;
  }
  @media (min-width: 5000px) {
    font-size: 3.75rem;
  }
`;

export const InputPhone = styled(PhoneInput)<{ $invalid: boolean }>`
  ${controlStyles}
  min-height: 42px;
  padding: ${({ theme }) => theme.padding.xsmall}
    ${({ theme }) => theme.padding.small};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.darkBlue};
  box-shadow: ${({ theme }) => theme.shadow.xsmall};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  --PhoneInput-color--focus: ${({ theme }) => theme.colors.accent};
  --PhoneInputCountryFlag-height: 1em;
  --PhoneInputCountrySelect-marginRight: 0.75em;
  border-color: ${({ $invalid, theme }) =>
    $invalid ? theme.colors.mediumRed : "transparent"};

  .PhoneInputInput {
    min-width: 0;
    width: 100%;
    padding: 0;
    border: 0;
    outline: none;
    background: transparent;
    color: inherit;
    font: inherit;
  }

  .PhoneInputCountry {
    align-self: stretch;
    padding-right: 0.75em;
    border-right: 1px solid ${({ theme }) => theme.colors.grey};
  }

  .PhoneInputCountrySelect {
    font: inherit;
    color: ${({ theme }) => theme.colors.darkBlue};
  }

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.accent};
    box-shadow: 0 0 0 3px rgba(18, 109, 200, 0.2);
  }

  &.PhoneInput--disabled {
    cursor: wait;
    opacity: 0.65;
  }

  @media (min-width: 2500px) {
    min-height: 62px;
    font-size: 1.75rem;
    border-radius: ${({ theme }) => theme.borderRadius.regular};
    padding: ${({ theme }) => theme.padding.small};
  }
  @media (min-width: 3000px) {
    min-height: 74px;
    font-size: 2rem;
    border-radius: ${({ theme }) => theme.borderRadius.medium};
    padding: ${({ theme }) => theme.padding.small}
      ${({ theme }) => theme.padding.regular};
  }
  @media (min-width: 3400px) {
    min-height: 92px;
    font-size: 2.5rem;
    border-radius: ${({ theme }) => theme.borderRadius.large};
    padding: ${({ theme }) => theme.padding.regular}
      ${({ theme }) => theme.padding.medium};
  }
  @media (min-width: 3800px) {
    min-height: 112px;
    font-size: 3rem;
  }
  @media (min-width: 4500px) {
    font-size: 3.25rem;
  }
  @media (min-width: 5000px) {
    font-size: 3.75rem;
  }
`;

export const PhoneError = styled.small`
  color: ${({ theme }) => theme.colors.darkRed};
  font-size: 0.8em;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

export const TextareaControl = styled(InputTextarea)`
  ${controlStyles}
  min-height: 120px;
  padding: ${({ theme }) => theme.padding.xsmall}
    ${({ theme }) => theme.padding.small};
  resize: none;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.darkBlue};
  box-shadow: ${({ theme }) => theme.shadow.xsmall};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.accent};
    box-shadow: 0 0 0 3px rgba(18, 109, 200, 0.2);
  }

  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }

  @media (min-width: 2500px) {
    min-height: 150px;
    font-size: 1.75rem;
    border-radius: ${({ theme }) => theme.borderRadius.regular};
    padding: ${({ theme }) => theme.padding.small};
  }
  @media (min-width: 3000px) {
    font-size: 2rem;
    border-radius: ${({ theme }) => theme.borderRadius.medium};
    padding: ${({ theme }) => theme.padding.small}
      ${({ theme }) => theme.padding.regular};
  }
  @media (min-width: 3400px) {
    font-size: 2.5rem;
    border-radius: ${({ theme }) => theme.borderRadius.large};
    padding: ${({ theme }) => theme.padding.regular}
      ${({ theme }) => theme.padding.medium};
  }
  @media (min-width: 3800px) {
    font-size: 3rem;
  }
  @media (min-width: 4500px) {
    font-size: 3.25rem;
  }
  @media (min-width: 5000px) {
    font-size: 3.75rem;
  }
`;

export const ConsentBox = styled.div`
  display: flex;
  align-items: top;
  gap: ${({ theme }) => theme.spacing.regular};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};

  span {
    font-size: ${({ theme }) => theme.typography.medium.fontSize};
  }

  @media (max-width: 767px) {
    gap: ${({ theme }) => theme.spacing.small};
  }
  @media (max-width: 520px) {
    span {
      font-size: 0.8rem;
    }
  }

  @media (min-width: 2500px) {
    gap: ${({ theme }) => theme.spacing.regular};

    span {
      font-size: 1.35rem;
    }
  }
  @media (min-width: 3000px) {
    span {
      font-size: 1.75rem;
    }
  }
  @media (min-width: 3400px) {
    gap: 30px;

    span {
      font-size: 2rem;
    }
  }
  @media (min-width: 3800px) {
    span {
      font-size: 2.25rem;
    }
  }
  @media (min-width: 4500px) {
    span {
      font-size: 2.75rem;
    }
  }
  @media (min-width: 5000px) {
    span {
      font-size: 3.25rem;
    }
  }
`;

export const PersonalDataLink = styled(Link)`
  text-underline-offset: 4px;
  text-decoration-thickness: 0.05rem;
  color: inherit;
  transition: color 0.4s, text-decoration-color 0.4s;

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
    text-decoration-color: transparent;
  }
`;

export const StyledCheckbox = styled.input`
  position: relative;
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  margin: 0;
  appearance: none;
  border: 1px solid ${({ theme }) => theme.colors.grey};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background-color: ${({ theme }) => theme.colors.white};
  box-sizing: border-box;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease,
    box-shadow 0.2s ease, opacity 0.2s ease;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(18, 109, 200, 0.25);
  }

  &:checked {
    border-color: ${({ theme }) => theme.colors.accent};
    background-color: ${({ theme }) => theme.colors.accent};
  }

  &:checked::after {
    content: "";
    position: absolute;
    top: 8px;
    left: 8px;
    width: 12px;
    height: 7px;
    border-bottom: 2px solid ${({ theme }) => theme.colors.white};
    border-left: 2px solid ${({ theme }) => theme.colors.white};
    transform: rotate(-45deg);
  }

  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }

  @media (max-width: 520px) {
    width: 24px;
    height: 24px;
    border-radius: ${({ theme }) => theme.borderRadius.xsmall};

    &:checked::after {
      top: 6px;
      left: 6px;
      width: 9px;
      height: 5px;
    }
  }

  @media (min-width: 2500px) {
    width: 42px;
    height: 42px;

    &:checked::after {
      width: 20px;
      height: 12px;
      border-bottom: 3px solid ${({ theme }) => theme.colors.white};
      border-left: 3px solid ${({ theme }) => theme.colors.white};
    }
  }
  @media (min-width: 3000px) {
    width: 48px;
    height: 48px;

    &:checked::after {
      width: 24px;
      height: 16px;
    }
  }
  @media (min-width: 3400px) {
    width: 52px;
    height: 52px;

    &:checked::after {
      width: 30px;
      height: 20px;
    }
  }
  @media (min-width: 3800px) {
    width: 64px;
    height: 64px;

    &:checked::after {
      top: 8px;
      left: 8px;
      width: 40px;
      height: 28px;
      border-bottom: 4px solid ${({ theme }) => theme.colors.white};
      border-left: 4px solid ${({ theme }) => theme.colors.white};
    }
  }
  @media (min-width: 4500px) {
    width: 72px;
    height: 72px;

    &:checked::after {
      top: 8px;
      left: 8px;
      width: 50px;
      height: 32px;
    }
  }
  @media (min-width: 5000px) {
    width: 78px;
    height: 78px;

    &:checked::after {
      top: 8px;
      left: 8px;
      width: 60px;
      height: 36px;
      border-bottom: 5px solid ${({ theme }) => theme.colors.white};
      border-left: 5px solid ${({ theme }) => theme.colors.white};
    }
  }
`;

export const CaptchaContainer = styled.div`
  width: 100%;
  min-height: 100px;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borderRadius.small};

  > div {
    max-width: 100%;
  }
`;

export const ConfigurationMessage = styled.p`
  box-sizing: border-box;
  min-height: 100px;
  margin: 0;
  padding: ${({ theme }) => theme.padding.regular};
  border: 1px solid ${({ theme }) => theme.colors.mediumRed};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background-color: ${({ theme }) => theme.colors.lightRed};
  color: ${({ theme }) => theme.colors.darkRed};
  font-size: ${({ theme }) => theme.typography.regular.fontSize};
`;

export const StatusMessage = styled.p<{ $type: "success" | "error" }>`
  margin: 0;
  padding: ${({ theme }) => theme.padding.small};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background-color: ${({ $type, theme }) =>
    $type === "success" ? theme.colors.lightGreen : theme.colors.lightRed};
  color: ${({ $type, theme }) =>
    $type === "success" ? theme.colors.darkGreen : theme.colors.darkRed};
  font-size: ${({ theme }) => theme.typography.regular.fontSize};
`;

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;

  @media (max-width: 520px) {
    button {
      width: 100%;
      inline-size: 100%;
      max-width: none;
    }
  }

  @media (min-width: 2500px) {
    margin-top: 10px;
  }
  @media (min-width: 3000px) {
    margin-top: 20px;
  }
  @media (min-width: 3400px) {
    margin-top: 30px;
  }
`;
